import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { findEntityVersion, reorderFurnitureInCache, spliceFurnitureIntoCache } from "helpers/quotationCache.helper"
import { buildCommentDeleteItems } from "helpers/batchFurniture.helper"
import {
	asCustomFurniture,
	asOtherFurniture,
} from "components/ui/customWebviewer/FurnitureModelLabel/furnitureModel.helpers"
import AnnotationService from "services/annotations.service"
import type { TRightPanelMode } from "../quotationViewer.helpers"
import type {
	useBatchUpdateFurniture,
	useBatchUpdateFurnitureCategory,
	useBatchUpdateFurnitureCustomModelNumber,
	useBatchSelectArbitraryModelNumber,
	useBatchUpdateFurnitureConfig,
	useBatchUpdateFurnitureCountertopAttachments,
	useBatchUpdateFurnitureFixedPrice,
	useBatchUpdateFurnitureComment,
	useBatchDeleteFurnitureComment,
	useBatchUpdateFurnitureCustomization,
	useBatchDeleteFurnitureCustomization,
} from "hooks/api/quotation/quotations.hook"
import type {
	TBatchConfigPatch,
	TBatchCountertopAttachmentsPatch,
	TBatchDeleteFurnitureCommentResponse,
	TBatchDeleteFurnitureCustomizationResponse,
	TBatchSelectArbitraryModelNumberResponse,
	TBatchUpdateFurnitureCategoryResponse,
	TBatchUpdateFurnitureCustomModelNumberResponse,
	TBatchUpdateFurnitureCommentResponse,
	TBatchUpdateFurnitureConfigResponse,
	TBatchUpdateFurnitureCountertopAttachmentsResponse,
	TBatchUpdateFurnitureCustomizationResponse,
	TBatchUpdateFurnitureFixedPriceResponse,
	TBatchUpdateFurnitureResponse,
	TFurniture,
	TFurnitureCountertopAttachments,
	TFurnitureCustomization,
	TFurnitureEstimationConfig,
	TFurnitureOrdering,
	TPageElevationData,
	TRelatedCategoryValue,
	TSelectedModelNumber,
} from "types/elevationReferences.type"
import type { TCustomModelNumber } from "types/customModelNumbers.type"
import type { TQuotationDetail } from "types/quotations.type"

/**
 * Batch furniture editing (REQ-403) — the behaviour behind the `furniture-batch` right panel.
 *
 * Owns which objects the panel is editing, and every commit it can make. Eight endpoints back it;
 * which one a field routes through is decided here, not in the component:
 *
 *  - dimensions, name, delivery date, model number in a shared category → `PATCH …/furniture/`
 *  - model number from another category (or any pick on a mixed-category selection)
 *    → `PATCH …/furniture/select-arbitrary-model-number/`
 *  - category (including "move it all to Other") → `PATCH …/furniture/related-category/`
 *  - attachments → `PATCH …/furniture/configuration/`
 *  - price → `PATCH …/furniture/fixed-price/`
 *  - notes → `PUT …/furniture/comment/`, or `DELETE …/furniture/comment/?items=` when blanked
 *  - customization (REQ-431) → `PUT …/furniture/customization/` to set (one request per group when
 *    the field NOT being edited disagrees across the selection — see `commitCustomization`'s doc
 *    comment), `DELETE …/furniture/customization/?items=` to remove
 *
 * All eight are transactional and all-or-nothing: a 400 means nothing changed. Every commit reports
 * back whether it was applied, so the panel can put a rejected field's displayed value back — its
 * own resync effects cannot, because a rejection leaves the derived shared value untouched.
 *
 * Two mechanisms keep concurrent edits honest, and they are needed TOGETHER:
 *
 *  - requests are SERIALIZED through one promise chain, so a second edit fired before the first
 *    response lands runs after it rather than alongside it;
 *  - `{ id, version }` items are read from the LIVE query cache inside the task, not from a
 *    render-time snapshot — a queued task runs before React has necessarily re-rendered, so the
 *    cache is the only source already carrying the versions the previous response bumped.
 *
 * Either one alone still sends a pre-increment version and earns a `400 Version mismatch`. This is
 * also why this does NOT use the per-entity mutation queue: one request covers many entities, so a
 * per-entity queue has no single key to serialize on.
 *
 * `isBatchEditPending` is a REPORTING flag for the footer and the confirm modal — it reflects
 * whether anything is in flight and gates nothing. Serialization is the chain's job, not its.
 *
 * ON `ordering`, because the asymmetry with the single-object sidebar looks like a bug and is not:
 * the plain batch PATCH carries NO `ordering`, while the single-object PATCH carries it as a
 * REQUIRED field. Render order is documented as "sorted by cabinet type group then by x0 position",
 * so only a CATEGORY change or a MOVE can re-sort a group. The single-object endpoint also accepts
 * `x0/y0/x1/y1`, so it can move an object and must report the new order; the batch endpoint accepts
 * only model number, name, dimensions and delivery date — none of which are in the sort key — so there is nothing
 * for it to report. The two batch endpoints that DO change a category (`related-category/` and
 * `select-arbitrary-model-number/`) both return `ordering`, and this hook applies it. So a
 * same-category batch model-number pick cannot leave the sidebars ordered stale: do not "fix" this
 * by adding a refetch.
 */
export type BatchFurnitureEditContext = {
	numericQuotationIdRef: MutableRefObject<number | undefined>
	/** Reactive page data — the panel's furniture is re-derived from it on every cache write. */
	relatedObjects: TPageElevationData[] | undefined
	rightPanelModeRef: MutableRefObject<TRightPanelMode>
	setRightPanelMode: (mode: TRightPanelMode) => void
	openReferencePanel: () => void
	closeReferencePanel: () => void
	/** True while a canvas mode (connect-objects, an "add a detection" drag) owns the document. */
	isCanvasModeActive: () => boolean
	/** Hand-over target when a batch shrinks to exactly one object. */
	handleFurnitureClick: (furniture: TFurniture, options?: { scroll?: boolean }) => void
	batchUpdateFurnitureAsync: ReturnType<typeof useBatchUpdateFurniture>["mutateAsync"]
	batchUpdateCategoryAsync: ReturnType<typeof useBatchUpdateFurnitureCategory>["mutateAsync"]
	batchUpdateCustomModelNumberAsync: ReturnType<typeof useBatchUpdateFurnitureCustomModelNumber>["mutateAsync"]
	batchSelectArbitraryModelNumberAsync: ReturnType<typeof useBatchSelectArbitraryModelNumber>["mutateAsync"]
	batchUpdateConfigAsync: ReturnType<typeof useBatchUpdateFurnitureConfig>["mutateAsync"]
	/** REQ-449. */
	batchUpdateCountertopAttachmentsAsync: ReturnType<typeof useBatchUpdateFurnitureCountertopAttachments>["mutateAsync"]
	batchUpdateFixedPriceAsync: ReturnType<typeof useBatchUpdateFurnitureFixedPrice>["mutateAsync"]
	batchUpdateCommentAsync: ReturnType<typeof useBatchUpdateFurnitureComment>["mutateAsync"]
	batchDeleteCommentAsync: ReturnType<typeof useBatchDeleteFurnitureComment>["mutateAsync"]
	batchUpdateCustomizationAsync: ReturnType<typeof useBatchUpdateFurnitureCustomization>["mutateAsync"]
	batchDeleteCustomizationAsync: ReturnType<typeof useBatchDeleteFurnitureCustomization>["mutateAsync"]
	queryClient: QueryClient
	formatError: (error: unknown) => void
}

export const useBatchFurnitureEdit = (ctx: BatchFurnitureEditContext) => {
	const {
		numericQuotationIdRef,
		relatedObjects,
		rightPanelModeRef,
		setRightPanelMode,
		openReferencePanel,
		closeReferencePanel,
		isCanvasModeActive,
		handleFurnitureClick,
		batchUpdateFurnitureAsync,
		batchUpdateCategoryAsync,
		batchUpdateCustomModelNumberAsync,
		batchSelectArbitraryModelNumberAsync,
		batchUpdateConfigAsync,
		batchUpdateCountertopAttachmentsAsync,
		batchUpdateFixedPriceAsync,
		batchUpdateCommentAsync,
		batchDeleteCommentAsync,
		batchUpdateCustomizationAsync,
		batchDeleteCustomizationAsync,
		queryClient,
		formatError,
	} = ctx

	const [batchFurnitureIds, setBatchFurnitureIds] = useState<number[]>([])
	const batchFurnitureIdsRef = useRef<number[]>([])
	batchFurnitureIdsRef.current = batchFurnitureIds
	const [isBatchEditPending, setIsBatchEditPending] = useState(false)
	/**
	 * "Don't show this again" for the category-change confirmation (§4.5). Lives here rather than in
	 * the panel so it survives the panel being closed and reopened — it is a decision about the
	 * session, not about one visit to the sidebar.
	 */
	const [skipCategoryConfirm, setSkipCategoryConfirm] = useState(false)

	/**
	 * The objects the panel is editing, resolved from the cache on every render so the panel and the
	 * requests it sends always carry current versions. Ids the cache no longer holds (deleted
	 * elsewhere) simply drop out.
	 */
	const furnitureById = useMemo(() => {
		const byId = new Map<number, TFurniture>()
		for (const page of relatedObjects ?? []) {
			for (const item of page.furniture) byId.set(item.id, item)
		}
		return byId
	}, [relatedObjects])
	// Ref'd so the selection sync can look ids up without depending on the map's identity, which
	// changes on every cache write.
	const furnitureByIdRef = useRef(furnitureById)
	furnitureByIdRef.current = furnitureById

	const batchFurniture = useMemo<TFurniture[]>(() => {
		if (batchFurnitureIds.length === 0) return []
		return batchFurnitureIds
			.map((id) => furnitureById.get(id))
			.filter((item): item is TFurniture => !!item)
	}, [batchFurnitureIds, furnitureById])
	// Mirrored so the selection sync stays STABLE: it is called from the orchestrator's
	// annotationSelected listener, and a callback whose identity changed on every cache write would
	// re-register that listener each time.
	const batchFurnitureRef = useRef<TFurniture[]>([])
	batchFurnitureRef.current = batchFurniture

	const closeBatchFurniture = useCallback(() => {
		setBatchFurnitureIds([])
		setRightPanelMode(null)
		closeReferencePanel()
	}, [setRightPanelMode, closeReferencePanel])

	// A batch delete (or any other removal) can empty the panel out from under the user. Nothing is
	// left to edit at that point, so close rather than render "0 objects selected" — and drop the
	// stale ids even when some other flow already moved the panel off batch mode, so they can't come
	// back the next time it opens.
	useEffect(() => {
		if (batchFurnitureIds.length === 0 || batchFurniture.length > 0) return
		if (rightPanelModeRef.current === "furniture-batch") {
			closeBatchFurniture()
			return
		}
		setBatchFurnitureIds([])
	}, [batchFurniture.length, batchFurnitureIds.length, rightPanelModeRef, closeBatchFurniture])

	/**
	 * Reconciles the panel with the live canvas selection. Called on EVERY selection change (select
	 * and deselect alike), next to the toolbar's own sync:
	 *
	 *  - two or more furniture selected, ALL ON ONE PAGE → the batch panel opens (or updates) on
	 *    exactly them;
	 *  - two or more spanning several pages → no panel. Every batch endpoint rejects a multi-page
	 *    selection with a 400, so there is not one field here that could save; opening would offer an
	 *    editor where nothing works. Same rule the floating toolbar applies to Connect (erefs are
	 *    page-bound), and it also keeps the panel's own page-scoped eref dropdown honest;
	 *  - exactly one → hand over to the single-object sidebar, which is the panel for one object;
	 *  - none → leave the panel as it was. A deselect happens for all sorts of reasons the user did
	 *    not mean as "close this" (an empty-space click, a mutation that clears the selection), and
	 *    the single-object sidebar stays open across those too.
	 */
	const syncBatchFurnitureSelection = useCallback(() => {
		if (isCanvasModeActive()) return
		const ids = AnnotationService.getSelectedFurnitureIds()
		if (ids.length > 1) {
			// Page check before anything else — see the doc comment above. Ids the cache doesn't hold
			// are ignored rather than treated as a page of their own; they contribute no page to
			// disagree about, and the empty-selection effect below deals with them.
			const pages = new Set<number>()
			for (const id of ids) {
				const page = furnitureByIdRef.current.get(id)?.page_number
				if (page != null) pages.add(page)
			}
			if (pages.size > 1) {
				// A panel the user has just grown across a page boundary is abandoned rather than left
				// pointing at a selection none of its fields can save.
				if (rightPanelModeRef.current === "furniture-batch") closeBatchFurniture()
				return
			}
			// Compared as a SET: Apryse gives no ordering guarantee across calls, and re-setting state
			// for a reshuffle of the same objects would remount the panel mid-edit.
			const previous = batchFurnitureIdsRef.current
			const isSame = ids.length === previous.length && ids.every((id) => previous.includes(id))
			if (!isSame) setBatchFurnitureIds(ids)
			setRightPanelMode("furniture-batch")
			openReferencePanel()
			return
		}
		if (rightPanelModeRef.current !== "furniture-batch") return
		if (ids.length === 0) return
		// Only hand over when the surviving selection is one of OUR objects — i.e. the batch really
		// shrank. A single selection that was never in the batch belongs to whatever raised it (a
		// left-sidebar row, a jump step, a plain canvas click), and that flow is opening the
		// single-object sidebar in this very tick; acting on it here would close the panel from under
		// the user, because `rightPanelModeRef` still reads "furniture-batch" until the next render.
		const single = batchFurnitureRef.current.find((item) => item.id === ids[0])
		if (!single) return
		setBatchFurnitureIds([])
		// `scroll: false` — the object is one the user just ctrl-clicked, so it is already on screen
		// and in view. Re-centring the document under a selection gesture reads as the viewer lurching
		// on its own, which is why every other selection-driven call here suppresses it too.
		handleFurnitureClick(single, { scroll: false })
	}, [
		isCanvasModeActive,
		setRightPanelMode,
		openReferencePanel,
		rightPanelModeRef,
		closeBatchFurniture,
		handleFurnitureClick,
	])

	/**
	 * `{ id, version }` items for the current panel selection, versions read from the LIVE query
	 * cache at call time — which is inside the task, after any earlier request has applied its
	 * response.
	 *
	 * Deliberately NOT derived from `batchFurniture`: that array is whatever the closure captured when
	 * the commit callback was created, so two edits fired before the first response landed would both
	 * send pre-increment versions and the second would be rejected. Ids the cache no longer holds drop
	 * out, exactly as they do from `batchFurniture`.
	 */
	const buildItems = useCallback(
		(quotationId: number): { id: number; version: number }[] => {
			const cached = queryClient.getQueryData<AxiosResponse<TQuotationDetail>>(["quotation", quotationId])
			const items: { id: number; version: number }[] = []
			for (const id of batchFurnitureIdsRef.current) {
				const version = findEntityVersion(cached?.data, "furniture", id)
				if (version !== undefined) items.push({ id, version })
			}
			return items
		},
		[queryClient],
	)

	/**
	 * Tail of the serialized request chain. Every task waits on this, and replaces it — so edits are
	 * applied in the order the user made them and each one sees the previous one's version bumps.
	 */
	const chainRef = useRef<Promise<unknown>>(Promise.resolve())
	/**
	 * How many commits are outstanding. A plain boolean set in a `finally` would be cleared by the
	 * FIRST task to settle while a queued one was still running, unlatching the footer mid-flight.
	 */
	const inflightRef = useRef(0)

	/**
	 * Runs one batch request behind whatever is already queued, owning the pending flag and the
	 * failure path. Resolves to whether the request was APPLIED: a 400 leaves the backend untouched,
	 * so a failure needs the toast, a refetch to re-seed the versions the rejection may have
	 * invalidated, and a `false` for the caller to restore the field with.
	 */
	const runBatch = useCallback(
		(task: (quotationId: number) => Promise<void>): Promise<boolean> => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return Promise.resolve(false)
			inflightRef.current += 1
			setIsBatchEditPending(true)
			// The chain is kept SETTLED (errors swallowed into the tail) so one rejected request can
			// never poison the requests queued behind it — each still reports its own outcome below.
			const run = chainRef.current.then(
				() => task(qid),
				() => task(qid),
			)
			chainRef.current = run.then(
				() => undefined,
				() => undefined,
			)
			return run.then(
				() => {
					inflightRef.current -= 1
					if (inflightRef.current === 0) setIsBatchEditPending(false)
					return true
				},
				(e: unknown) => {
					inflightRef.current -= 1
					if (inflightRef.current === 0) setIsBatchEditPending(false)
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
					return false
				},
			)
		},
		[numericQuotationIdRef, formatError, queryClient],
	)

	/**
	 * Merges a batch response's full furniture payloads into the cache, re-sorting every ordering
	 * group the response names (a selection can span several, one per eref plus the unconnected page
	 * objects) and repainting the canvas labels when asked.
	 *
	 * `asOther` force-clears the three category levels on the way in: `spliceFurnitureIntoCache`
	 * MERGES, so a stale category would otherwise survive a move to Other and keep the old label.
	 */
	const applyFurnitureBatch = useCallback(
		(
			quotationId: number,
			items: TFurniture[],
			options: {
				ordering?: TFurnitureOrdering[]
				repaintLabels?: boolean
				asOther?: boolean
				asCustom?: boolean
			} = {},
		) => {
			// `spliceFurnitureIntoCache` merges, so a CONVERSION has to force-clear what the objects used
			// to be — otherwise the stale category (or Stevens model number) survives the splice and the
			// objects keep rendering as cabinets. Same reason the single-object paths normalise.
			const normalized = options.asCustom
				? items.map(asCustomFurniture)
				: options.asOther
					? items.map(asOtherFurniture)
					: items
			queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
				["quotation", quotationId],
				(cached) => {
					let next = cached
					for (const item of normalized) next = spliceFurnitureIntoCache(next, item)
					for (const group of options.ordering ?? []) next = reorderFurnitureInCache(next, group)
					return next
				},
			)
			if (options.repaintLabels) {
				for (const item of normalized) AnnotationService.updateFurnitureAnnotation(item)
			}
			// A highlight's FILL is a separate repaint from its label: `updateFurnitureAnnotation`
			// deliberately skips `applyStyle`, so a response that moves objects between statuses —
			// a category change resets them to `work_in_progress` — would leave the canvas showing
			// the old colour while the sidebar already reads the new one. The single-object path
			// gets away with not doing this because it invalidates and refetches, which rebuilds
			// every annotation from scratch; a batch splices, so it has to repaint here.
			//
			// Deliberately NOT gated on "did the status change": the drift this fixes means a
			// painted colour cannot be trusted to match the cached status, so repainting every
			// time is what makes it self-correcting. Same unconditional fan-out the batch status
			// endpoint already does.
			for (const item of normalized) {
				if (item.status) AnnotationService.updateFurnitureEntityStatus(item.id, item.status)
			}
		},
		[queryClient],
	)

	/**
	 * One dimension for the whole selection.
	 *
	 * A CLEARED field reports `false` rather than sending: the endpoint types `width` / `height` /
	 * `depth` as a non-nullable `integer >= 0`, so a batch has no way to unset a dimension. Reporting
	 * failure is what lets the panel put the stored value back — dropping it silently would leave the
	 * input blank over a backend that still holds a number, and no resync could ever repair that
	 * (nothing changed, so the derived shared value is identical).
	 */
	const commitDimension = useCallback(
		(key: "width" | "height" | "depth", value: number | null): Promise<boolean> => {
			if (value === null || batchFurniture.length === 0) return Promise.resolve(false)
			return runBatch(async (qid) => {
				const response = await batchUpdateFurnitureAsync({
					quotationId: qid,
					body: { furniture: buildItems(qid), [key]: value },
				})
				const items = (response?.data as TBatchUpdateFurnitureResponse | undefined)?.furniture ?? []
				applyFurnitureBatch(qid, items)
			})
		},
		[batchFurniture.length, runBatch, batchUpdateFurnitureAsync, buildItems, applyFurnitureBatch],
	)

	/**
	 * Delivery date override for the whole selection — same `PATCH …/furniture/` endpoint dimensions
	 * use, and genuinely nullable there (REQ-410's batch counterpart): `null` clears the override so
	 * every listed item goes back to inheriting its elevation's date, an id sets an explicit one.
	 * Unlike `commitDimension`, a clear does not need the "can't unset, report failure so the panel
	 * restores the field" workaround — this field has a real null on the wire.
	 *
	 * Commits immediately on select rather than on blur (no local text state to keep in sync), so —
	 * same as `commitModelNumber` and `ElevationReferenceSelect`'s `onErefChange` — a rejection needs
	 * no client-side revert: `runBatch`'s failure path already invalidates the cache, and the Select
	 * reads its value straight from the derived shared field.
	 */
	const commitDeliveryDate = useCallback(
		(deliveryDateId: number | null): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			return runBatch(async (qid) => {
				const response = await batchUpdateFurnitureAsync({
					quotationId: qid,
					body: { furniture: buildItems(qid), delivery_date_id: deliveryDateId },
				})
				const items = (response?.data as TBatchUpdateFurnitureResponse | undefined)?.furniture ?? []
				applyFurnitureBatch(qid, items)
			})
		},
		[batchFurniture.length, runBatch, batchUpdateFurnitureAsync, buildItems, applyFurnitureBatch],
	)

	/**
	 * One Custom Model Number for the whole selection (#1346).
	 *
	 * Like its single-object counterpart this is ONE request even when the selection is not custom
	 * yet: the endpoint converts every listed item and links the definition in the same transaction,
	 * so there is never a category call to make first.
	 */
	const commitCustomModelNumber = useCallback(
		(customModelNumberId: number): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			return runBatch(async (qid) => {
				const response = await batchUpdateCustomModelNumberAsync({
					quotationId: qid,
					body: { furniture: buildItems(qid), custom_model_number_id: customModelNumberId },
				})
				const data = response?.data as TBatchUpdateFurnitureCustomModelNumberResponse | undefined
				applyFurnitureBatch(qid, data?.furniture ?? [], {
					ordering: data?.ordering,
					repaintLabels: true,
					asCustom: true,
				})
			})
		},
		[batchFurniture.length, runBatch, batchUpdateCustomModelNumberAsync, buildItems, applyFurnitureBatch],
	)

	/**
	 * Applies a model-number pick to the whole selection.
	 *
	 * `useArbitraryEndpoint` comes from the picker and is a property of the PICK: it is set when the
	 * row came from the catalogue-wide scope, or when it belongs to a different `/categories/` node
	 * than the selection's own. Either way the plain batch PATCH would reject it — it demands that
	 * every listed item already share the model number's category — so the pick goes through
	 * `select-arbitrary-model-number/`, which applies the model number AND moves every item's
	 * category in one transaction. A mixed-category selection always lands on that path, because the
	 * picker has no object category to offer it in the first place.
	 */
	const commitModelNumber = useCallback(
		(
			model: TSelectedModelNumber,
			options: { useArbitraryEndpoint: boolean; customModelNumber?: TCustomModelNumber | null },
		): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			// A custom pick is a different endpoint AND a conversion, so it never reaches the two
			// Stevens branches below.
			const customModelNumber = options.customModelNumber ?? null
			if (customModelNumber) return commitCustomModelNumber(customModelNumber.id)
			return runBatch(async (qid) => {
				const body = { furniture: buildItems(qid), selected_model_number_id: model.id }
				if (options.useArbitraryEndpoint) {
					const response = await batchSelectArbitraryModelNumberAsync({ quotationId: qid, body })
					const data = response?.data as TBatchSelectArbitraryModelNumberResponse | undefined
					applyFurnitureBatch(qid, data?.furniture ?? [], { ordering: data?.ordering, repaintLabels: true })
					return
				}
				const response = await batchUpdateFurnitureAsync({ quotationId: qid, body })
				const items = (response?.data as TBatchUpdateFurnitureResponse | undefined)?.furniture ?? []
				applyFurnitureBatch(qid, items, { repaintLabels: true })
			})
		},
		[
			batchFurniture.length,
			runBatch,
			buildItems,
			batchSelectArbitraryModelNumberAsync,
			batchUpdateFurnitureAsync,
			applyFurnitureBatch,
			commitCustomModelNumber,
		],
	)

	/**
	 * One category for the whole selection; `null` moves it all to Other / fixed-price mode. The
	 * response carries complete furniture payloads plus an ordering group per affected group, so the
	 * cascade the backend performs (model numbers disconnected, price configs created or dropped,
	 * status reset to `work_in_progress`) lands without a refetch.
	 */
	const commitCategory = useCallback(
		(relatedCategoryId: TRelatedCategoryValue): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			return runBatch(async (qid) => {
				const response = await batchUpdateCategoryAsync({
					quotationId: qid,
					body: { furniture: buildItems(qid), related_category_id: relatedCategoryId },
				})
				const data = response?.data as TBatchUpdateFurnitureCategoryResponse | undefined
				applyFurnitureBatch(qid, data?.furniture ?? [], {
					ordering: data?.ordering,
					repaintLabels: true,
					asOther: relatedCategoryId === null,
					// #1346 — the literal string, the third value this endpoint accepts.
					asCustom: relatedCategoryId === "custom",
				})
			})
		},
		[batchFurniture.length, runBatch, batchUpdateCategoryAsync, buildItems, applyFurnitureBatch],
	)

	/**
	 * One attachments field for the whole selection. The response is config-shaped (keyed by
	 * `furniture_id`), not furniture-shaped, so it is merged into each item's `estimation_config`
	 * rather than spliced wholesale — and it carries the item's WHOLE resulting config, including the
	 * fields the request omitted, which is what keeps a partly-Mixed section honest afterwards.
	 */
	const commitConfig = useCallback(
		(patch: TBatchConfigPatch): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			return runBatch(async (qid) => {
				// Resolved inside the task, so a request queued behind another one merges onto the
				// config that one just wrote rather than onto a render-time snapshot of it.
				const currentById = furnitureByIdRef.current
				const response = await batchUpdateConfigAsync({
					quotationId: qid,
					body: { furniture: buildItems(qid), ...patch },
				})
				const rows = (response?.data as TBatchUpdateFurnitureConfigResponse | undefined)?.furniture ?? []
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
					["quotation", qid],
					(cached) => {
						let next = cached
						for (const row of rows) {
							const current = currentById.get(row.furniture_id)
							const baseConfig: TFurnitureEstimationConfig = current?.estimation_config ?? {
								furniture_id: row.furniture_id,
								number_of_fillers: 0,
								number_of_finishing_ends: 0,
								lock_count: null,
								has_locks: false,
								adjusted_shelves: null,
							}
							next = spliceFurnitureIntoCache(next, {
								id: row.furniture_id,
								version: row.version,
								estimation_config: {
									...baseConfig,
									has_locks: row.has_locks,
									number_of_fillers: row.number_of_fillers,
									number_of_finishing_ends: row.number_of_finishing_ends,
									adjusted_shelves: row.adjusted_shelves,
								},
							})
						}
						return next
					},
				)
			})
		},
		[batchFurniture, runBatch, batchUpdateConfigAsync, buildItems, queryClient],
	)

	/**
	 * REQ-449 batch counterpart of `commitConfig`, for the Grommets / Wall Cleats fields on a
	 * countertop-only selection. The response is keyed by `furniture_id`, not furniture-shaped, and
	 * each row NESTS its counts under `countertop_attachments` — reading them flat off the row
	 * spliced `undefined` into the cache, which the single-object sidebar then rendered as 0 on
	 * the next selection (#1447 review). The nested object is the truth after the write, so it
	 * replaces the cached one; only a row missing it falls back to the local base + the patch.
	 */
	const commitCountertopAttachments = useCallback(
		(patch: TBatchCountertopAttachmentsPatch): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			return runBatch(async (qid) => {
				const currentById = furnitureByIdRef.current
				const response = await batchUpdateCountertopAttachmentsAsync({
					quotationId: qid,
					body: { furniture: buildItems(qid), ...patch },
				})
				const rows =
					(response?.data as TBatchUpdateFurnitureCountertopAttachmentsResponse | undefined)?.furniture ?? []
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
					["quotation", qid],
					(cached) => {
						let next = cached
						for (const row of rows) {
							const current = currentById.get(row.furniture_id)
							const baseAttachments: TFurnitureCountertopAttachments = current?.countertop_attachments ?? {
								grommets: 0,
								wall_cleats: 0,
							}
							next = spliceFurnitureIntoCache(next, {
								id: row.furniture_id,
								version: row.version,
								countertop_attachments: row.countertop_attachments ?? { ...baseAttachments, ...patch },
							})
						}
						return next
					},
				)
			})
		},
		[batchFurniture, runBatch, batchUpdateCountertopAttachmentsAsync, buildItems, queryClient],
	)

	/**
	 * One fixed price for the whole selection. Other / fixed-price objects only.
	 *
	 * `null` is a real value here, not "nothing to send": the endpoint documents `null` as CLEARING
	 * the price on every listed item, and bulk-clearing is the main reason the batch variant exists.
	 * So a cleared field sends, exactly as a typed one does — guarding it out would leave the input
	 * visibly empty while the backend kept the old price, with no resync able to repair it (the
	 * derived shared value never changed, so the resync effect never refires).
	 */
	const commitPrice = useCallback(
		(price: number | null): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			return runBatch(async (qid) => {
				// Read inside the task for the same reason as in commitConfig: the custom name this
				// endpoint does not return has to come from the latest cache, not a stale snapshot.
				const currentById = furnitureByIdRef.current
				const response = await batchUpdateFixedPriceAsync({
					quotationId: qid,
					body: { furniture: buildItems(qid), fixed_price: price },
				})
				const rows = (response?.data as TBatchUpdateFurnitureFixedPriceResponse | undefined)?.furniture ?? []
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
					["quotation", qid],
					(cached) => {
						let next = cached
						for (const row of rows) {
							next = spliceFurnitureIntoCache(next, {
								id: row.furniture_id,
								version: row.version,
								fixed_price_config: {
									// Written through as-is, null included — a cleared price IS null, and coercing it
									// to 0 would render "$0.00" on the label for an object that has no price.
									fixed_price: row.fixed_price,
									// The endpoint answers with the price only; the custom name is a separate field
									// that this call does not touch, so it is carried over from the cache.
									name: currentById.get(row.furniture_id)?.fixed_price_config?.name ?? null,
								},
							})
						}
						return next
					},
				)
			})
		},
		[batchFurniture, runBatch, batchUpdateFixedPriceAsync, buildItems, queryClient],
	)

	/**
	 * One custom name for the whole selection. Other / fixed-price objects only.
	 *
	 * A blanked field sends `name: null`, which CLEARS the custom name — the endpoint rejects a blank
	 * STRING but documents null as the way to unset, and the single-object sidebar already clears this
	 * way (`bodyName = trimmed === "" ? null : trimmed`). REQ-403 requires the batch edit to match the
	 * per-object one, so refusing the clear here would make the two disagree and leave the field blank
	 * over a stored name.
	 */
	const commitName = useCallback(
		(name: string): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			const trimmed = name.trim()
			return runBatch(async (qid) => {
				const response = await batchUpdateFurnitureAsync({
					quotationId: qid,
					body: { furniture: buildItems(qid), name: trimmed === "" ? null : trimmed },
				})
				const items = (response?.data as TBatchUpdateFurnitureResponse | undefined)?.furniture ?? []
				applyFurnitureBatch(qid, items, { repaintLabels: true })
			})
		},
		[batchFurniture.length, runBatch, batchUpdateFurnitureAsync, buildItems, applyFurnitureBatch],
	)

	/**
	 * One estimation comment for the whole selection — cleared with the DELETE variant when the
	 * textarea is blanked, exactly as the single-object sidebar does. Unlike the single-item DELETE,
	 * an item that has no comment is a no-op here rather than a 404, so blanking a partly-commented
	 * selection is safe.
	 */
	const commitNotes = useCallback(
		(notes: string): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			const isClearing = notes.trim() === ""
			return runBatch(async (qid) => {
				if (isClearing) {
					const response = await batchDeleteCommentAsync({
						quotationId: qid,
						items: buildCommentDeleteItems(buildItems(qid)),
					})
					const rows = (response?.data as TBatchDeleteFurnitureCommentResponse | undefined)?.furniture ?? []
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
						["quotation", qid],
						(cached) => {
							let next = cached
							for (const row of rows) {
								next = spliceFurnitureIntoCache(next, {
									id: row.id,
									version: row.version,
									estimation_config_comment: null,
								})
							}
							return next
						},
					)
					return
				}
				const response = await batchUpdateCommentAsync({
					quotationId: qid,
					body: { furniture: buildItems(qid), comment: notes },
				})
				const rows = (response?.data as TBatchUpdateFurnitureCommentResponse | undefined)?.furniture ?? []
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
					["quotation", qid],
					(cached) => {
						let next = cached
						for (const row of rows) {
							next = spliceFurnitureIntoCache(next, {
								id: row.id,
								version: row.version,
								estimation_config_comment: row.comment,
							})
						}
						return next
					},
				)
			})
		},
		[batchFurniture.length, runBatch, buildItems, batchDeleteCommentAsync, batchUpdateCommentAsync, queryClient],
	)

	/**
	 * One customization (REQ-431) for the whole selection — creates it where an item has none and
	 * OVERWRITES it where it does, exactly as the single-object PUT does per item. `edits` carries
	 * ONLY the field(s) the caller actually wants to change (`resolveCustomizationEdits`'s job) — the
	 * header's `+` is the one caller that always provides both, `{ note: "", price_adjustment: null }`.
	 *
	 * The endpoint itself has no partial-update mode — every request still needs both fields — so an
	 * omitted field is resolved to each ITEM'S OWN current value rather than dropped, and furniture is
	 * grouped by the resulting (note, price_adjustment) pair before sending: everyone sharing a group
	 * gets one PUT. When the omitted field already agrees across the selection (or both fields are
	 * provided) this collapses to the single request it always was; it only splits into several when
	 * the field being preserved genuinely disagrees, which is exactly the case that used to send the
	 * OTHER field's Mixed baseline (`""`/`null`) to every object and clobber its real, differing
	 * values. Reads through `furnitureByIdRef`/`batchFurnitureIdsRef` rather than the closed-over
	 * `batchFurniture`, for the same reason `buildItems` does: the live cache, not a render-time
	 * snapshot, so a task queued behind another commit groups on values that commit already applied.
	 */
	const commitCustomization = useCallback(
		(edits: Partial<TFurnitureCustomization>): Promise<boolean> => {
			if (batchFurniture.length === 0) return Promise.resolve(false)
			const noteProvided = "note" in edits
			const priceProvided = "price_adjustment" in edits
			return runBatch(async (qid) => {
				const itemsById = new Map(buildItems(qid).map((item) => [item.id, item]))
				const groups = new Map<
					string,
					{ note: string; price_adjustment: number | null; items: { id: number; version: number }[] }
				>()
				for (const id of batchFurnitureIdsRef.current) {
					const item = itemsById.get(id)
					if (!item) continue
					const current = furnitureByIdRef.current.get(id)?.customization
					const note = noteProvided ? (edits.note as string) : (current?.note ?? "")
					const price_adjustment = priceProvided ? (edits.price_adjustment as number | null) : (current?.price_adjustment ?? null)
					const key = JSON.stringify([note, price_adjustment])
					const group = groups.get(key)
					if (group) group.items.push(item)
					else groups.set(key, { note, price_adjustment, items: [item] })
				}
				for (const group of groups.values()) {
					const response = await batchUpdateCustomizationAsync({
						quotationId: qid,
						body: { furniture: group.items, price_adjustment: group.price_adjustment, note: group.note },
					})
					const rows = (response?.data as TBatchUpdateFurnitureCustomizationResponse | undefined)?.furniture ?? []
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
						["quotation", qid],
						(cached) => {
							let next = cached
							for (const row of rows) {
								next = spliceFurnitureIntoCache(next, {
									id: row.id,
									version: row.version,
									customization: { note: row.note, price_adjustment: row.price_adjustment },
								})
							}
							return next
						},
					)
				}
			})
		},
		[batchFurniture.length, runBatch, batchUpdateCustomizationAsync, buildItems, queryClient],
	)

	/**
	 * Clears the customization from every selected object (REQ-431's "remove"). Unlike the
	 * single-item DELETE (404 on an object with none), an item with no customization is a no-op
	 * here, so removing over a partly-customized selection is always safe to send.
	 */
	const removeCustomization = useCallback((): Promise<boolean> => {
		if (batchFurniture.length === 0) return Promise.resolve(false)
		return runBatch(async (qid) => {
			const response = await batchDeleteCustomizationAsync({
				quotationId: qid,
				items: buildCommentDeleteItems(buildItems(qid)),
			})
			const rows = (response?.data as TBatchDeleteFurnitureCustomizationResponse | undefined)?.furniture ?? []
			queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
				["quotation", qid],
				(cached) => {
					let next = cached
					for (const row of rows) {
						next = spliceFurnitureIntoCache(next, { id: row.id, version: row.version, customization: null })
					}
					return next
				},
			)
		})
	}, [batchFurniture.length, runBatch, batchDeleteCustomizationAsync, buildItems, queryClient])

	return {
		/** The live objects the panel edits — empty when the panel is not in batch mode. */
		batchFurniture,
		isBatchEditPending,
		skipCategoryConfirm,
		setSkipCategoryConfirm,
		syncBatchFurnitureSelection,
		closeBatchFurniture,
		commitDimension,
		commitDeliveryDate,
		commitModelNumber,
		commitCustomModelNumber,
		commitCategory,
		commitConfig,
		commitCountertopAttachments,
		commitPrice,
		commitName,
		commitNotes,
		commitCustomization,
		removeCustomization,
	}
}
